import { ReactElement, ReactNode } from 'react'
import { Box, Typography } from '@mui/material'
import { SxProps, Theme } from '@mui/material/styles'

export interface PageHeaderProps {
  icon?: ReactElement
  title: ReactNode
  titleFontWeight?: 600 | 700
  titleColor?: string
  titleComponent?: string
  subtitle?: ReactNode
  subtitleVariant?: 'body1' | 'body2'
  subtitleSx?: SxProps<Theme>
  action?: ReactNode
  sx?: SxProps<Theme>
}

const PageHeader = ({
  icon,
  title,
  titleFontWeight,
  titleColor,
  titleComponent,
  subtitle,
  subtitleVariant = 'body1',
  subtitleSx,
  action,
  sx,
}: PageHeaderProps) => {
  const titleBlock = (
    <Box>
      <Typography
        variant="h4"
        {...(titleComponent ? { component: titleComponent } : {})}
        gutterBottom={!!subtitle}
        sx={{ fontWeight: titleFontWeight, color: titleColor }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant={subtitleVariant} color="textSecondary" sx={subtitleSx}>
          {subtitle}
        </Typography>
      )}
    </Box>
  )

  // Only introduce a flex row when there's an icon or action to lay out
  // alongside the title - otherwise stay a plain block container so text
  // wrapping/width behaves exactly as it did before extraction.
  if (!icon && !action) {
    return <Box sx={sx}>{titleBlock}</Box>
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', ...sx }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {icon}
        {titleBlock}
      </Box>
      {action}
    </Box>
  )
}

export default PageHeader
